import React, { useState } from 'react';
import styled, { css } from 'styled-components/macro';
import { LinkWithSearchParams as Link } from 'components/base/Link/Link';
import first from 'lodash.first';
import { rgba } from 'polished';
import delve from 'dlv';

import CompareSearch from './CompareSearch/CompareSearch';
import CompareList from './CompareList/CompareList';
import Layout from 'components/styled/Layout/Layout';

import pluralize from 'helpers/pluralize';
import modularScale from 'styles/helpers/modular-scale';
import getEntityUrl from 'url-constructors/getEntityUrl';

import { definitionLabel } from 'styles/base-classes';

import { ReactComponent as IconPlus } from 'icons/icon-plus.svg';
import { EntityTypes, ContextTopics } from 'context/entityContext';
import { History } from 'history';

export const closeLinkStyle = css`
  color: white;
  line-height: 1;
  font-size: ${modularScale(-1)};
  flex-shrink: 0;
  text-align: right;
  margin-left: 1em;

  &:hover {
    text-decoration: underline;
  }
`;

const Wrapper = styled('div')`
  margin: ${({ theme }): string => `${theme.spacing.xlarge} 0 1em`};
`;

export const Header = styled('header')`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: ${({ theme }): string => theme.spacing.large};
  color: white;
`;

export const Title = styled('h2')`
  ${definitionLabel};
  font-weight: 600;
  margin: 0;
  color: inherit;
`;

export const CloseLink = styled(Link)`
  ${closeLinkStyle};
`;

export const ToggleButton = styled('button')`
  color: white;
  padding: ${({ theme }): string => theme.spacing.small};
  transition: ${({ theme }): string =>
    `all ${theme.transitionDuration} ${theme.timingFunction}`};
  text-align: left;
  display: flex;
  align-items: center;
  width: 100%;
  margin: 0;

  svg {
    flex-shrink: 0;
    margin-right: ${({ theme }): string => theme.spacing.small};
  }

  &:not([disabled]) {
    &:hover {
      background-color: ${({ theme }): string => rgba(theme.NightBlue, 0.15)};
    }

    &:active {
      background-color: ${({ theme }): string => rgba(theme.NightBlue, 0.2)};
    }
  }

  &[disabled] {
    opacity: 0.5;
  }
`;

export const ToggleSearchButton = styled(ToggleButton)`
  margin-top: 0.5em;

  svg {
    margin-right: ${({ theme }): string => theme.spacing.small};
  }

  @media screen and (min-width: 30em) {
    padding: ${({ theme }): string =>
      `${theme.spacing.small} ${theme.spacing.large}`};
    margin-top: 1em;

    svg {
      margin-right: ${({ theme }): string => theme.spacing.large};
    }
  }
`;

interface IEntityCompareHeaderProps {
  entityIds: string[];
  entityType: EntityTypes;
  entitiesById: any;
  topic: ContextTopics;
  history: History;
  reloadEntity: (...args: any[]) => any;
}

export interface IEntitiesById {
  [key: string]: IReduxEntity;
}

export interface IReduxEntity {
  entity: any;
  error: boolean;
  loading: boolean;
  metadata: any;
}

/** The EntityHeader when in Compare mode */
const EntityCompareHeader: React.FC<IEntityCompareHeaderProps> = (props) => {
  const {
    entityIds,
    entityType,
    entitiesById,
    topic,
    history,
    reloadEntity,
  } = props;

  const [isSearchVisible, setSearchVisibility] = useState(
    entityIds.length === 1
  );

  function toggleSearchVisibility(): void {
    setSearchVisibility((isSearchVisible) => !isSearchVisible);
  }

  return (
    <Wrapper data-pw="entity-compare-wrapper">
      <Layout.MaxWidth>
        <Header data-pw="entity-compare-header">
          <Title>{`Compare ${pluralize(entityType)}`}</Title>
          <CloseLink
            to={getEntityUrl({
              entityId: first(entityIds),
              entityType,
              topic,
              parentEntityId: delve(
                entitiesById,
                `${[first(entityIds)]}.entity.parent_group_id`
              ),
            })}
            preserveSearch={true}
          >
            Close compare
          </CloseLink>
        </Header>
      </Layout.MaxWidth>
      <Layout.MaxWidth>
        <CompareList
          entityIds={entityIds}
          entityType={entityType}
          entitiesById={entitiesById}
          topic={topic}
          reloadEntity={reloadEntity}
        />
        {!isSearchVisible && entityIds.length < 4 && (
          <ToggleSearchButton onClick={toggleSearchVisibility}>
            <IconPlus /> Add {entityType} to compare
          </ToggleSearchButton>
        )}
        {isSearchVisible && (
          <CompareSearch
            entityIds={entityIds}
            entityType={entityType}
            topic={topic}
            history={history}
            toggleSearchVisibility={toggleSearchVisibility}
            index={entityIds.length}
          />
        )}
      </Layout.MaxWidth>
    </Wrapper>
  );
};

export default EntityCompareHeader;
